/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { internalSessionIssuersRotate } from "../funcs/internalSessionIssuersRotate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { InternalSessionIssuer } from "../models/components/internalsessionissuer.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RotateInternalSessionIssuerRequest,
  RotateInternalSessionIssuerSecurity,
} from "../models/operations/rotateinternalsessionissuer.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RotateInternalSessionIssuerMutationVariables = {
  request: RotateInternalSessionIssuerRequest;
  security?: RotateInternalSessionIssuerSecurity | undefined;
  options?: RequestOptions;
};

export type RotateInternalSessionIssuerMutationData = InternalSessionIssuer;

export type RotateInternalSessionIssuerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * rotate internalSessionIssuers
 *
 * @remarks
 * Rotate the issuer's signing key without changing its URI. A routine rotation takes two calls with the same active_kid: the first publishes a new key and returns rotation_ready_at, the second activates it after that time. The hourly sweep also activates a ready key on its own while the issuer is enabled and not paused, so a later second call may find the rotation already done and change nothing; a paused issuer can still be activated with the second call. With compromised set, every current key is revoked and withdrawn from the key set first, leaving the issuer keyless; the revoked keys' signing material is then disabled and, while the issuer is rolled out and the organization has a verified email domain, a fresh key activated. If either later step fails the issuer stays keyless or reports kms_cleanup_pending, and repeating the call retries it. Repeats naming a key that a compromised rotation already replaced change nothing; naming a key the sweep revoked after a routine rotation still contains its successor. Requires org:admin; a routine rotation also needs an enabled issuer, while a compromised rotation only needs an existing issuer and its revocation is never rate limited.
 */
export function useRotateInternalSessionIssuerMutation(
  options?: MutationHookOptions<
    RotateInternalSessionIssuerMutationData,
    RotateInternalSessionIssuerMutationError,
    RotateInternalSessionIssuerMutationVariables
  >,
): UseMutationResult<
  RotateInternalSessionIssuerMutationData,
  RotateInternalSessionIssuerMutationError,
  RotateInternalSessionIssuerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRotateInternalSessionIssuerMutation(client, options),
    ...options,
  });
}

export function mutationKeyRotateInternalSessionIssuer(): MutationKey {
  return ["@gram/client", "internalSessionIssuers", "rotate"];
}

export function buildRotateInternalSessionIssuerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RotateInternalSessionIssuerMutationVariables,
  ) => Promise<RotateInternalSessionIssuerMutationData>;
} {
  return {
    mutationKey: mutationKeyRotateInternalSessionIssuer(),
    mutationFn: function rotateInternalSessionIssuerMutationFn({
      request,
      security,
      options,
    }): Promise<RotateInternalSessionIssuerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(internalSessionIssuersRotate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
